import { useMemo } from "react"
import { size, toLower } from "lodash"
import useResource from "hooks/resources"
import { SelectedInstrument, SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { IsoDate } from "helpers/types/dates"

export interface FilterOption {
  value: string
  label: string
  count: number
}

export interface AssetFilterOptions {
  assetClasses: FilterOption[]
  assetSubclasses: FilterOption[]
  classifications: Record<string, FilterOption[]>
  countries: FilterOption[]
  currencies: FilterOption[]
  tags: Record<string, FilterOption[]>
  positionAttributes: Record<string, FilterOption[]>
  long: boolean
  short: boolean
}

export function useAssetFilters(
  modelId: string,
  accountAlias: string,
  instrument: SelectedTypedInstrument,
  startDate: IsoDate,
  endDate: IsoDate,
  isIntraday = false,
) {
  return useResource<AssetFilterOptions | null>({
    resource: "assetFilters",
    url: () => {
      if (!modelId || !accountAlias || !instrument || !startDate || !endDate) {
        return null
      }
      const params = { startDate, endDate, intraday: isIntraday.toString() }
      const qs = (new URLSearchParams(params)).toString()

      switch (instrument.type) {
        case "BASKET":
          return `/api/${accountAlias}/m/${modelId}/r/swap/${instrument.id}/filters?${qs}`
        case "ETF":
          return `/api/${accountAlias}/m/${modelId}/etfs/${instrument.id}/filters?${qs}`
        case "INDEX":
          return `/api/${accountAlias}/m/${modelId}/indices/${instrument.id}/filters?${qs}`
        case "PORTFOLIO":
          return `/api/${accountAlias}/m/${modelId}/r/portfolio/${instrument.id}/filters?${qs}`
        default:
          return null
      }
    },
  }, "assetFilters")
}

interface SecurityFilterOptions {
  modelProviderIds: string[]
  swapTickers: string[]
}

export function useFilterSecurities(modelId: string, account: string, instrument: SelectedInstrument | undefined, startDate: IsoDate, endDate: IsoDate, isExpanded = false, intraday = false, intradayLastRefresh?: string) {
  const definition = useMemo(() => ({
    resource: `dashboardSecurityIds-${instrument?.type}-${instrument?.id}-${modelId}-${startDate}-${endDate}-${isExpanded}`,
    as: null,
    url: () => {
      if (!account || !instrument?.id || !startDate || !endDate || !modelId) {
        return null
      }
      const { id } = instrument
      const type = toLower(instrument.type) === "basket" ? "swap" : toLower(instrument.type)

      switch (type) {
        case "etf":
          return {
            id: `etfSecurityIds-${id}-${modelId}-${startDate}-${endDate}-${isExpanded}`,
            url: `/api/${account}/m/${modelId}/etfs/${id}/securities?${new URLSearchParams({ startDate, endDate, expand: isExpanded.toString() })}`,
          }
        case "index":
          return {
            id: `indexSecurityIds-${id}-${modelId}-${startDate}-${endDate}-${isExpanded}`,
            url: `/api/${account}/m/${modelId}/indices/${id}/securities?${new URLSearchParams({ startDate, endDate, expand: isExpanded.toString() })}`,
          }
        default:
          return {
            id: `resourceSecurityIds-${type}-${id}-${modelId}-${startDate}-${endDate}${intraday ? intradayLastRefresh : ""}-${isExpanded}`,
            url: `/api/${account}/m/${modelId}/r/${type}/${id}/securities?${new URLSearchParams({ startDate, endDate, intraday: intraday.toString(), expand: isExpanded.toString() })}`,
          }
      }
    },
  }), [account, instrument, startDate, endDate, modelId, isExpanded, intraday, intradayLastRefresh])
  return useResource<SecurityFilterOptions>(definition, "dashboardSecurityIds")
}

interface FilterSecurityDescriptors {
  ticker: string
  mic: string
  country: string
  modelProviderId: string
  hasComposition: boolean
}

export function useSecuritiesDescriptors(account: string, modelId: string, securitiesIds: string[], endDate: IsoDate) {
  return useResource<FilterSecurityDescriptors[] | null>({
    resource: `portfolioSecuritiesDescriptors-${JSON.stringify(securitiesIds)}-${modelId}-${endDate}`,
    url: () => {
      if (size(securitiesIds) === 0) {
        return null
      }

      return {
        url: `/api/${account}/m/${modelId}/securities/descriptors?date=${endDate}`,
        id: `securitiesDescriptors${account}${modelId}${endDate}${JSON.stringify(securitiesIds)}`,
        payload: { ids: securitiesIds },
      }
    },
  }, "portfolioSecuritiesDescriptors")
}
